import { Component } from '@theme/component';
import { prefersReducedMotion } from '@theme/utilities';
import { enableSheetDrag } from '@theme/sheet-drag';

const mobile = matchMedia('(max-width: 749px)');

/**
 * <plw-mobile-filters> — behaviour for the mobile collection filter UI
 * (see snippets/plw-mobile-filters.liquid).
 *
 * The "Filter" toggle and each per-filter sheet are native <details> elements, so
 * open/close (and morph-survival across live filtering) is handled by the browser.
 * This component owns only the bits HTML can't do natively:
 *   - close a sheet from its backdrop / "View" button,
 *   - close the whole panel from "Close",
 *   - clear a single filter ("Clear") and re-run the AJAX filter,
 *   - lock body scroll while a bottom-sheet is open, and close on Escape,
 *   - drag-to-dismiss on each bottom sheet (the shared PLW sheet-drag).
 *
 * Bound declaratively in markup via on:click="/closeSheet" etc.
 */
class PlwMobileFilters extends Component {
  /** @type {Map<HTMLElement, { reset: () => void, destroy: () => void }>} */
  #drags = new Map();

  connectedCallback() {
    super.connectedCallback();
    // `toggle` doesn't bubble, so listen in the capture phase.
    this.addEventListener('toggle', this.#onToggle, true);
    this.addEventListener('keydown', this.#onKeyDown);
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.removeEventListener('toggle', this.#onToggle, true);
    this.removeEventListener('keydown', this.#onKeyDown);
    this.#setScrollLock(false);
    for (const drag of this.#drags.values()) drag.destroy();
    this.#drags.clear();
  }

  /**
   * Close the sheet that contains the event target (falls back to any open sheet).
   * @param {Event} [event]
   */
  closeSheet(event) {
    const target = event?.target instanceof Element ? event.target : null;
    const sheet = target?.closest('.plw-sheet') ?? this.querySelector('.plw-sheet[open]');
    if (sheet instanceof HTMLDetailsElement) sheet.open = false;
  }

  /**
   * Close every open sheet and collapse the filter panel.
   */
  closePanel() {
    for (const sheet of this.querySelectorAll('.plw-sheet[open]')) {
      if (sheet instanceof HTMLDetailsElement) sheet.open = false;
    }
    const panel = this.querySelector('.plw-mfilters__disclosure');
    if (panel instanceof HTMLDetailsElement) panel.open = false;
  }

  /**
   * Clear a single filter's selections, then re-run the AJAX filter.
   * @param {Event} [event]
   */
  clearSheet(event) {
    const target = event?.target instanceof Element ? event.target : null;
    const sheet = target?.closest('.plw-sheet');
    if (!sheet) return;

    for (const input of sheet.querySelectorAll('input')) {
      if (!(input instanceof HTMLInputElement)) continue;
      if (input.type === 'checkbox' || input.type === 'radio') {
        input.checked = false;
      } else {
        input.value = '';
      }
    }

    const form = this.querySelector('facets-form-component');
    // FacetsFormComponent exposes updateFilters() as a public method.
    if (form && typeof form.updateFilters === 'function') form.updateFilters();
  }

  #onToggle = (/** @type {Event} */ event) => {
    this.#setScrollLock(this.querySelector('.plw-sheet[open]') != null);

    const sheet = event.target;
    if (sheet instanceof HTMLDetailsElement && sheet.classList.contains('plw-sheet') && sheet.open) {
      this.#wireDrag(sheet);
    }
  };

  /**
   * Wire the shared sheet-drag onto a sheet's panel, lazily on each open —
   * morph.js can replace nodes across live filtering, and re-wiring on open
   * picks the replacements back up.
   * @param {HTMLDetailsElement} sheet
   */
  #wireDrag(sheet) {
    const panel = sheet.querySelector('.plw-sheet__panel');
    const handle = sheet.querySelector('.plw-sheet__handle');
    if (!(panel instanceof HTMLElement) || !(handle instanceof HTMLElement)) return;
    if (this.#drags.has(panel)) return;

    const drag = enableSheetDrag({
      dialog: panel,
      handle,
      surface: panel,
      isMobile: () => mobile.matches,
      onDismiss: () => this.#dismissSheet(sheet, panel),
    });
    this.#drags.set(panel, drag);
  }

  /**
   * Drag-dismiss: slide out from the release point (mirroring the dialog
   * sheets' closing keyframes), then let <details> close natively.
   * @param {HTMLDetailsElement} sheet
   * @param {HTMLElement} panel
   */
  #dismissSheet(sheet, panel) {
    const close = () => {
      sheet.open = false;
      this.#drags.get(panel)?.reset();
    };
    if (prefersReducedMotion()) return close();

    const duration = panel.style.getPropertyValue('--plw-sheet-dismiss-duration') || '200ms';
    panel.style.transition = `transform ${duration} ease-in`;
    void panel.offsetHeight;
    panel.style.transform = 'translateY(100%)';
    panel.addEventListener('transitionend', close, { once: true });
    // Fallback in case transitionend never fires (hidden tab etc.); close is idempotent.
    setTimeout(close, parseFloat(duration) + 100);
  }

  #onKeyDown = (event) => {
    if (event.key !== 'Escape') return;
    const openSheet = this.querySelector('.plw-sheet[open]');
    if (openSheet) {
      this.closeSheet(event);
      event.stopPropagation();
    }
  };

  /** @param {boolean} locked */
  #setScrollLock(locked) {
    document.body.classList.toggle('plw-sheet-scroll-lock', locked);
  }
}

if (!customElements.get('plw-mobile-filters')) {
  customElements.define('plw-mobile-filters', PlwMobileFilters);
}
